import React, { useMemo } from 'react';
import { Card, Tag, Spin, Empty, Typography } from 'antd';
import type { ChainListItem } from '../../../../services/devApi';
import { domainColorMap, chainStatusColorMap, shortStatusMap } from './helpers';

const { Text } = Typography;

interface Props {
  chains: ChainListItem[];
  loading: boolean;
  selectedChainId: string;
  onSelect: (chainId: string) => void;
}

/** 左欄：按 businessDomain 分組的 chain 列表 */
const ChainListSidebar: React.FC<Props> = ({ chains, loading, selectedChainId, onSelect }) => {
  // 按 businessDomain 分組（保持出現順序）
  const groups = useMemo(() => {
    const order: string[] = [];
    const byDomain: Record<string, ChainListItem[]> = {};
    chains.forEach((c) => {
      const domain = c.businessDomain || 'Other';
      if (!byDomain[domain]) {
        byDomain[domain] = [];
        order.push(domain);
      }
      byDomain[domain].push(c);
    });
    return order.map((domain) => ({ domain, items: byDomain[domain] }));
  }, [chains]);

  return (
    <Card
      size="small"
      title={`業務鏈條 (${chains.length})`}
      style={{ width: 280, flexShrink: 0 }}
      bodyStyle={{ padding: 8, maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}
    >
      <Spin spinning={loading}>
        {!loading && chains.length === 0 && (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="沒有鏈條資料" />
        )}
        {groups.map(({ domain, items }) => (
          <div key={domain} className="mb-2">
            <div className="px-2 py-1">
              <Tag color={domainColorMap[domain] || 'default'} style={{ marginRight: 0 }}>
                {domain}
              </Tag>
            </div>
            {items.map((c) => {
              const selected = c.chainId === selectedChainId;
              return (
                <div
                  key={c.chainId}
                  onClick={() => onSelect(c.chainId)}
                  className="px-2 py-1.5 rounded cursor-pointer"
                  style={{
                    background: selected ? '#e6f4ff' : undefined,
                    border: selected ? '1px solid #91caff' : '1px solid transparent',
                    marginBottom: 2,
                  }}
                >
                  <div className="flex items-center justify-between gap-1">
                    <span className="font-mono font-bold" style={{ fontSize: 12 }}>
                      {c.chainId}
                    </span>
                    <Tag
                      color={chainStatusColorMap[c.currentStatus] || 'default'}
                      style={{ marginRight: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}
                    >
                      {shortStatusMap[c.currentStatus] || c.currentStatus}
                    </Tag>
                  </div>
                  <Text
                    type={selected ? undefined : 'secondary'}
                    ellipsis
                    style={{ fontSize: 12, display: 'block' }}
                  >
                    {c.chainName}
                  </Text>
                </div>
              );
            })}
          </div>
        ))}
      </Spin>
    </Card>
  );
};

export default ChainListSidebar;
